/**
 * batch-69 actiepunten (FO-514, spec 214) - "Actiepunten app": het register van
 * handmatige inrichtingsacties die uit elke oplevering voortkomen.
 *
 * De gedeelde bron voor api, backoffice en de seed: de enums met labels, de
 * modules, de rechtenregels (wie ziet het register, wie mag een punt
 * behandelen, wie beslist), het schema van een seed-bestand
 * (`packages/db/seed/actiepunten/batch-NN.json`), de Zod-schema's van de
 * mutaties (de api valideert ermee, het formulier vooraf) en de DTO's. Alles
 * hier is puur: geen DB, geen IO.
 *
 * De kern: een beheerhulpmiddel, geen tweede waarheid. Een punt met een check
 * staat op GEREED omdat zijn bron dat zegt (de dagelijkse meting), en valt
 * vanzelf terug naar OPEN zodra die bron weer leeg is. Waar geen check mogelijk
 * is, is dat expliciet ("niet automatisch vast te stellen") en staat erbij wie
 * het wanneer handmatig vaststelde.
 */
import { z } from 'zod'
import { queryBoolean } from './zod-query'

// ─── Enums (spiegel van de Prisma-enums) ────────────────────────────────────

export const ACTIEPUNT_SOORTEN = [
  'UPLOAD',
  'INSTELLING',
  'ONTVANGERS',
  'DATA_INVOER',
  'BESLISPUNT',
  'SCRIPT',
  'CONTROLE',
] as const
export type ActiepuntSoort = (typeof ACTIEPUNT_SOORTEN)[number]

export const ACTIEPUNT_SOORT_LABELS: Record<ActiepuntSoort, string> = {
  UPLOAD: 'Upload',
  INSTELLING: 'Instelling',
  ONTVANGERS: 'Ontvangers',
  DATA_INVOER: 'Data-invoer',
  BESLISPUNT: 'Beslispunt',
  SCRIPT: 'Script',
  CONTROLE: 'Controle',
}

export const ACTIEPUNT_STATUSSEN = [
  'OPEN',
  'BEZIG',
  'GEREED',
  'NIET_VAN_TOEPASSING',
  'GEBLOKKEERD',
] as const
export type ActiepuntStatus = (typeof ACTIEPUNT_STATUSSEN)[number]

export const ACTIEPUNT_STATUS_LABELS: Record<ActiepuntStatus, string> = {
  OPEN: 'Open',
  BEZIG: 'Bezig',
  GEREED: 'Gereed',
  NIET_VAN_TOEPASSING: 'Niet van toepassing',
  GEBLOKKEERD: 'Geblokkeerd',
}

/** Nog te doen: de standaardweergave van het register. */
export const ACTIEPUNT_OPEN_STATUSSEN = ['OPEN', 'BEZIG', 'GEBLOKKEERD'] as const satisfies readonly ActiepuntStatus[]
/** Afgerond: de tab "Afgerond" (wie, wanneer, automatisch of handmatig). */
export const ACTIEPUNT_AFGEROND_STATUSSEN = ['GEREED', 'NIET_VAN_TOEPASSING'] as const satisfies readonly ActiepuntStatus[]

export function isActiepuntOpen(status: ActiepuntStatus): boolean {
  return (ACTIEPUNT_OPEN_STATUSSEN as readonly ActiepuntStatus[]).includes(status)
}

export const ACTIEPUNT_ROLLEN = [
  'PO',
  'HR',
  'ADMINISTRATIE',
  'SALARIS',
  'WAGENPARK',
  'MAGAZIJN',
  'BEHEER',
] as const
export type ActiepuntRol = (typeof ACTIEPUNT_ROLLEN)[number]

export const ACTIEPUNT_ROL_LABELS: Record<ActiepuntRol, string> = {
  PO: 'PO / directie',
  HR: 'HR',
  ADMINISTRATIE: 'Administratie',
  SALARIS: 'Salarisadministratie',
  WAGENPARK: 'Wagenpark',
  MAGAZIJN: 'Magazijn',
  BEHEER: 'Technisch beheer',
}

export const ACTIEPUNT_PRIORITEITEN = ['BLOKKEERT_FUNCTIE', 'BELANGRIJK', 'NETJES'] as const
export type ActiepuntPrioriteit = (typeof ACTIEPUNT_PRIORITEITEN)[number]

export const ACTIEPUNT_PRIORITEIT_LABELS: Record<ActiepuntPrioriteit, string> = {
  BLOKKEERT_FUNCTIE: 'Blokkeert een functie',
  BELANGRIJK: 'Belangrijk',
  NETJES: 'Netjes',
}

export const ACTIEPUNT_METING_UITKOMSTEN = [
  'GEREED',
  'NIET_GEREED',
  'DEELS_GEREED',
  'NIET_AUTOMATISCH',
  'CONTROLE_MISLUKT',
  'CONTROLE_ONBEKEND',
] as const
export type ActiepuntMetingUitkomst = (typeof ACTIEPUNT_METING_UITKOMSTEN)[number]

export const ACTIEPUNT_METING_UITKOMST_LABELS: Record<ActiepuntMetingUitkomst, string> = {
  GEREED: 'Meting: gereed',
  NIET_GEREED: 'Meting: niet gereed',
  DEELS_GEREED: 'Meting: meetbaar deel gereed, rest handmatig',
  NIET_AUTOMATISCH: 'Niet automatisch vast te stellen',
  CONTROLE_MISLUKT: 'Controle mislukt',
  CONTROLE_ONBEKEND: 'Controle niet beschikbaar',
}

export const ACTIEPUNT_GEREED_BRONNEN = [
  'AUTOMATISCH',
  'HANDMATIG',
  'HANDMATIG_AFWIJKEND',
  'BESLUIT',
  'INVENTARIS',
] as const
export type ActiepuntGereedBron = (typeof ACTIEPUNT_GEREED_BRONNEN)[number]

export const ACTIEPUNT_GEREED_BRON_LABELS: Record<ActiepuntGereedBron, string> = {
  AUTOMATISCH: 'Automatisch gemeten',
  HANDMATIG: 'Handmatig vastgesteld',
  HANDMATIG_AFWIJKEND: 'Handmatig vastgesteld, afwijkend van de meting',
  BESLUIT: 'Besluit vastgelegd',
  INVENTARIS: 'Volgens het rapport, nog niet gemeten',
}

export const ACTIEPUNT_HERKOMSTEN = ['SEED', 'HANDMATIG'] as const
export type ActiepuntHerkomst = (typeof ACTIEPUNT_HERKOMSTEN)[number]

export const ACTIEPUNT_AUDIT_ACTIES = [
  'AANGEMAAKT',
  'DEFINITIE_BIJGEWERKT',
  'STATUS_GEWIJZIGD',
  'AUTOMATISCH_GEREED',
  'HEROPEND',
  'METING_MISLUKT',
  'TOEGEWEZEN',
  'BESLUIT_VASTGELEGD',
  'VERVALLEN',
  'OPMERKING',
  'BEWERKT',
  'VERWIJDERD',
] as const
export type ActiepuntAuditActie = (typeof ACTIEPUNT_AUDIT_ACTIES)[number]

export const ACTIEPUNT_AUDIT_ACTIE_LABELS: Record<ActiepuntAuditActie, string> = {
  AANGEMAAKT: 'Aangemaakt',
  DEFINITIE_BIJGEWERKT: 'Omschrijving bijgewerkt (oplevering)',
  STATUS_GEWIJZIGD: 'Status gewijzigd',
  AUTOMATISCH_GEREED: 'Automatisch gereed (meting)',
  HEROPEND: 'Vanzelf heropend',
  METING_MISLUKT: 'Controle mislukt',
  TOEGEWEZEN: 'Toegewezen',
  BESLUIT_VASTGELEGD: 'Besluit vastgelegd',
  VERVALLEN: 'Vervallen (latere FO)',
  OPMERKING: 'Opmerking',
  BEWERKT: 'Bewerkt',
  VERWIJDERD: 'Verwijderd',
}

// ─── Modules (filter + groepering) ──────────────────────────────────────────

/**
 * Het onderdeel waar een punt bij hoort. De eerste acht zijn de secties van de
 * terugwerkende inventaris; de rest is er voor latere batches. Staat als tekst
 * in de database (geen enum-migratie bij een nieuwe module); dit is de lijst.
 */
export const ACTIEPUNT_MODULES = [
  'DOCUMENTEN',
  'FACTURATIE',
  'UREN',
  'MELDINGEN',
  'PAYROLL',
  'HRM',
  'MATERIEEL',
  'KLANTPORTAAL',
  'VERLOF',
  'PLANNING',
  'CRM',
  'SYSTEEM',
] as const
export type ActiepuntModule = (typeof ACTIEPUNT_MODULES)[number]

export const ACTIEPUNT_MODULE_LABELS: Record<ActiepuntModule, string> = {
  DOCUMENTEN: 'Huisstijl en documenten',
  FACTURATIE: 'Facturen en Finqle',
  UREN: 'Uren, pauze en werkbonnen',
  MELDINGEN: 'Meldingen en ontvangers',
  PAYROLL: 'Payroll en loonstroken',
  HRM: 'HRM',
  MATERIEEL: 'Materieel, uitrusting, voorraad en inkoop',
  KLANTPORTAAL: 'Klantenportaal, planning en overig',
  VERLOF: 'Verlof en verzuim',
  PLANNING: 'Planning',
  CRM: 'Relaties en CRM',
  SYSTEEM: 'Systeem en beheer',
}

export function isActiepuntModule(v: string): v is ActiepuntModule {
  return (ACTIEPUNT_MODULES as readonly string[]).includes(v)
}

export function actiepuntModuleLabel(v: string): string {
  return isActiepuntModule(v) ? ACTIEPUNT_MODULE_LABELS[v] : v
}

// ─── Rechten (puur; de api dwingt af, de UI toont alleen) ───────────────────

/** De gebruikersrollen zoals `User.role` (geen import uit de api). */
export type ActiepuntGebruikerRol =
  | 'MONTEUR'
  | 'TEAMLEIDER'
  | 'PLANNER'
  | 'HR'
  | 'SALARIS'
  | 'MANAGER'
  | 'ADMIN'
  | 'ACCOUNTANT'

/**
 * Wie het register ziet (spec 214 §4: ADMIN/HR/administratie; de PO zit onder
 * MANAGER). Backoffice-toegang is daarnaast verplicht. De ACCOUNTANT staat er
 * bewust niet in.
 */
export const ACTIEPUNT_KIJKER_ROLLEN = ['ADMIN', 'MANAGER', 'HR', 'SALARIS'] as const satisfies readonly ActiepuntGebruikerRol[]

export interface ActiepuntGebruiker {
  role: ActiepuntGebruikerRol | string
  hasBackofficeAccess: boolean
}

export function magActiepuntenZien(gebruiker: ActiepuntGebruiker): boolean {
  return (
    gebruiker.hasBackofficeAccess === true &&
    (ACTIEPUNT_KIJKER_ROLLEN as readonly string[]).includes(gebruiker.role)
  )
}

/**
 * Welke gebruikersrollen een verantwoordelijke rol dragen: zij zien het punt
 * "voor jou", krijgen de melding bij heropenen en mogen het behandelen (ADMIN
 * mag daarnaast altijd alles). Wagenpark, magazijn en technisch beheer hebben in
 * STREEP geen eigen backoffice-rol met toegang tot dit register: die punten
 * liggen bij beheer (ADMIN), en de PO wijst ze desgewenst aan een persoon toe.
 * Administratie en salaris zijn in STREEP dezelfde rol (SALARIS).
 */
export const ACTIEPUNT_ROL_GEBRUIKERSROLLEN: Record<ActiepuntRol, readonly ActiepuntGebruikerRol[]> = {
  PO: ['MANAGER'],
  HR: ['HR'],
  ADMINISTRATIE: ['SALARIS'],
  SALARIS: ['SALARIS'],
  WAGENPARK: ['ADMIN'],
  MAGAZIJN: ['ADMIN'],
  BEHEER: ['ADMIN'],
}

/** De verantwoordelijke rollen waar deze gebruiker (zonder toewijzing) voor staat. */
export function actiepuntRollenVanGebruiker(gebruiker: ActiepuntGebruiker): ActiepuntRol[] {
  if (!magActiepuntenZien(gebruiker)) return []
  return ACTIEPUNT_ROLLEN.filter((rol) =>
    (ACTIEPUNT_ROL_GEBRUIKERSROLLEN[rol] as readonly string[]).includes(gebruiker.role),
  )
}

/**
 * Mag deze gebruiker het punt behandelen (afvinken, niet van toepassing,
 * geblokkeerd, bezig, heropenen)? ADMIN altijd; anders de toegewezen persoon of
 * een drager van de verantwoordelijke rol (spec 214 §4). Een beslispunt SLUITEN
 * (afvinken, niet van toepassing) vraagt daarnaast `magActiepuntBesluiten`. Een
 * opmerking plaatsen mag iedereen die het register ziet (samenwerken aan een punt).
 */
export function magActiepuntBehandelen(
  gebruiker: ActiepuntGebruiker & { id: string },
  punt: { verantwoordelijkeRol: ActiepuntRol; toegewezenAanId: string | null },
): boolean {
  if (!magActiepuntenZien(gebruiker)) return false
  if (gebruiker.role === 'ADMIN') return true
  if (punt.toegewezenAanId && punt.toegewezenAanId === gebruiker.id) return true
  return actiepuntRollenVanGebruiker(gebruiker).includes(punt.verantwoordelijkeRol)
}

/**
 * Mag deze gebruiker een beslispunt beantwoorden? De PO/directie (spec 214 §4):
 * ADMIN, MANAGER, of wie de directie-vlag heeft (`Employee.isDirectie`).
 */
export function magActiepuntBesluiten(gebruiker: ActiepuntGebruiker & { isDirectie?: boolean }): boolean {
  if (!magActiepuntenZien(gebruiker)) return false
  return gebruiker.role === 'ADMIN' || gebruiker.role === 'MANAGER' || gebruiker.isDirectie === true
}

/** Mag deze gebruiker een punt toewijzen of zijn rol, prioriteit of deadline wijzigen? */
export function magActiepuntToewijzen(gebruiker: ActiepuntGebruiker): boolean {
  if (!magActiepuntenZien(gebruiker)) return false
  return gebruiker.role === 'ADMIN' || gebruiker.role === 'MANAGER'
}

// ─── Gedeelde veldschema's ──────────────────────────────────────────────────

/** Een NL-kalenderdag als 'yyyy-MM-dd' (deadline). */
export const actiepuntDatumSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'Gebruik een datum als jjjj-mm-dd' })
  .refine((v) => {
    const d = new Date(`${v}T00:00:00.000Z`)
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v
  }, { message: 'Dit is geen bestaande datum' })

/**
 * Een deeplink is altijd een relatief backoffice-pad (`/instellingen/...`), met
 * eventueel een query. Nooit een extern adres, nooit `//host`, nooit een schema:
 * het register mag niemand naar buiten sturen.
 */
export const actiepuntDeeplinkSchema = z
  .string()
  .trim()
  .max(300, { message: 'De link is te lang (maximaal 300 tekens)' })
  .regex(/^\/(?!\/)[A-Za-z0-9\-._~/%]*(\?[A-Za-z0-9\-._~=&%+:,]*)?$/, {
    message: 'Gebruik een pad binnen de backoffice, beginnend met / (geen extern adres)',
  })

/** Het padgedeelte van een deeplink (zonder query), voor de route-controle. */
export function actiepuntDeeplinkPad(deeplink: string): string {
  const pad = deeplink.split('?')[0] ?? deeplink
  return pad.length > 1 && pad.endsWith('/') ? pad.slice(0, -1) : pad
}

export const ACTIEPUNT_CODE_RE = /^[A-Z][A-Z0-9]*(?:-[A-Z0-9]+)+$/

/** Parameters van een check: platte waarden (de registry valideert per sleutel). */
export const actiepuntCheckParamsSchema = z.record(
  z.string(),
  z.union([z.string(), z.number(), z.boolean(), z.array(z.string())]),
)
export type ActiepuntCheckParams = z.infer<typeof actiepuntCheckParamsSchema>

export const actiepuntCheckVerwijzingSchema = z.object({
  sleutel: z.string().min(1).max(80),
  params: actiepuntCheckParamsSchema.optional(),
  /**
   * De check meet alleen een voorwaarde van het punt (bv. de kenmerken bij
   * "kenmerken invullen en het script draaien"). Een slagende check geeft dan
   * "meetbaar deel gereed" en zet het punt nooit vanzelf op gereed; een
   * falende check houdt het open (of heropent het).
   */
  voorwaarde: z.boolean().optional(),
})
export type ActiepuntCheckVerwijzing = z.infer<typeof actiepuntCheckVerwijzingSchema>

// ─── Seed-bestand (packages/db/seed/actiepunten/batch-NN.json) ──────────────

/** Beginstatus in de seed. ONBEKEND = OPEN met de markering "te controleren". */
export const ACTIEPUNT_SEED_BEGINSTATUSSEN = ['OPEN', 'GEREED', 'ONBEKEND'] as const
export type ActiepuntSeedBeginstatus = (typeof ACTIEPUNT_SEED_BEGINSTATUSSEN)[number]

export const actiepuntSeedPuntSchema = z
  .object({
    code: z.string().regex(ACTIEPUNT_CODE_RE, { message: 'Code in hoofdletters met koppeltekens, bv. B62-BRIEFPAPIER-ACTIVEREN' }).max(80),
    /** Volgnummer in de terugwerkende inventaris van FO-514 (bijlage 214). */
    inventarisNr: z.number().int().min(1).max(999).optional(),
    titel: z.string().trim().min(3).max(200),
    /** Uitleg hoe: wat er moet gebeuren, waar, en wanneer het klaar is. */
    omschrijving: z.string().trim().min(10).max(4000),
    soort: z.enum(ACTIEPUNT_SOORTEN),
    nevenSoorten: z.array(z.enum(ACTIEPUNT_SOORTEN)).max(3).optional(),
    module: z.enum(ACTIEPUNT_MODULES),
    bron: z.object({
      fo: z.string().regex(/^FO-\d+$/).optional(),
      /** Rapportregel(s), bv. "b62 briefpapier:122, b62:459". */
      verwijzing: z.string().trim().min(1).max(300),
    }),
    rol: z.enum(ACTIEPUNT_ROLLEN),
    prioriteit: z.enum(ACTIEPUNT_PRIORITEITEN),
    deadline: actiepuntDatumSchema.optional(),
    deeplink: actiepuntDeeplinkSchema.optional(),
    check: actiepuntCheckVerwijzingSchema.optional(),
    /** Verplicht zonder check: waarom het niet automatisch vast te stellen is. */
    geenCheckReden: z.string().trim().min(10).max(500).optional(),
    beginstatus: z.enum(ACTIEPUNT_SEED_BEGINSTATUSSEN),
    /** Waar de beginstatus op rust ("GEREED volgens b64:181 en b65:132"). */
    beginToelichting: z.string().trim().max(500).optional(),
    /** De aanname in een spec waar een besluit naar terugverwijst ("FO-473 A3"). */
    specAanname: z.string().trim().max(100).optional(),
    /** Een latere FO maakt het punt overbodig: de seed zet NIET_VAN_TOEPASSING. */
    vervalt: z
      .object({
        reden: z.string().trim().min(10).max(500),
        verwijzing: z.string().trim().min(1).max(100),
      })
      .optional(),
  })
  .superRefine((p, ctx) => {
    if (!p.check && !p.geenCheckReden) {
      ctx.addIssue({
        code: 'custom',
        path: ['geenCheckReden'],
        message: 'Zonder check hoort een reden waarom het punt niet automatisch vast te stellen is',
      })
    }
    if (p.check && p.geenCheckReden) {
      ctx.addIssue({
        code: 'custom',
        path: ['geenCheckReden'],
        message: 'Een punt met check heeft geen "geen check"-reden',
      })
    }
    if (p.nevenSoorten?.includes(p.soort)) {
      ctx.addIssue({ code: 'custom', path: ['nevenSoorten'], message: 'De hoofdsoort staat niet ook bij de nevensoorten' })
    }
    if (p.beginstatus === 'GEREED' && !p.beginToelichting) {
      ctx.addIssue({
        code: 'custom',
        path: ['beginToelichting'],
        message: 'Een punt dat gereed binnenkomt noemt de bron daarvan (rapport of commit)',
      })
    }
  })
export type ActiepuntSeedPunt = z.infer<typeof actiepuntSeedPuntSchema>

export const actiepuntSeedBestandSchema = z
  .object({
    batch: z.number().int().min(1).max(999),
    /** Waar de punten vandaan komen (inventaris, STATUS-OCHTEND van de batch). */
    bron: z.string().trim().min(3).max(300),
    punten: z.array(actiepuntSeedPuntSchema).min(1),
  })
  .superRefine((b, ctx) => {
    const prefix = `B${b.batch}-`
    const gezien = new Set<string>()
    b.punten.forEach((p, i) => {
      if (!p.code.startsWith(prefix)) {
        ctx.addIssue({
          code: 'custom',
          path: ['punten', i, 'code'],
          message: `De code van een punt uit batch ${b.batch} begint met ${prefix}`,
        })
      }
      if (gezien.has(p.code)) {
        ctx.addIssue({ code: 'custom', path: ['punten', i, 'code'], message: `Dubbele code ${p.code}` })
      }
      gezien.add(p.code)
    })
  })
export type ActiepuntSeedBestand = z.infer<typeof actiepuntSeedBestandSchema>

// ─── Lijst (query) ──────────────────────────────────────────────────────────

/** Tabs van het register: nog te doen, afgerond (wie/wanneer), of alles. */
export const ACTIEPUNT_WEERGAVEN = ['open', 'afgerond', 'alle'] as const
export type ActiepuntWeergave = (typeof ACTIEPUNT_WEERGAVEN)[number]

export const actiepuntenLijstQuerySchema = z.object({
  weergave: z.enum(ACTIEPUNT_WEERGAVEN).default('open'),
  status: z.enum(ACTIEPUNT_STATUSSEN).optional(),
  soort: z.enum(ACTIEPUNT_SOORTEN).optional(),
  module: z.enum(ACTIEPUNT_MODULES).optional(),
  rol: z.enum(ACTIEPUNT_ROLLEN).optional(),
  prioriteit: z.enum(ACTIEPUNT_PRIORITEITEN).optional(),
  batch: z.coerce.number().int().min(1).max(999).optional(),
  meting: z.enum(ACTIEPUNT_METING_UITKOMSTEN).optional(),
  teControleren: queryBoolean().optional(),
  heropend: queryBoolean().optional(),
  voorMij: queryBoolean().optional(),
  zoek: z.string().trim().max(100).optional(),
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
})
export type ActiepuntenLijstQuery = z.infer<typeof actiepuntenLijstQuerySchema>

/** De filters die in de URL staan (en ook de export-body vormen). */
export const ACTIEPUNT_FILTER_SLEUTELS = [
  'weergave',
  'status',
  'soort',
  'module',
  'rol',
  'prioriteit',
  'batch',
  'meting',
  'teControleren',
  'heropend',
  'voorMij',
  'zoek',
] as const
export type ActiepuntFilterSleutel = (typeof ACTIEPUNT_FILTER_SLEUTELS)[number]

// ─── Mutaties (één schema: api + formulier) ─────────────────────────────────

export const ACTIEPUNT_STATUS_ACTIES = [
  'AFVINKEN',
  'BEZIG',
  'NIET_VAN_TOEPASSING',
  'GEBLOKKEERD',
  'HEROPENEN',
] as const
export type ActiepuntStatusActie = (typeof ACTIEPUNT_STATUS_ACTIES)[number]

export const ACTIEPUNT_STATUS_ACTIE_LABELS: Record<ActiepuntStatusActie, string> = {
  AFVINKEN: 'Afvinken',
  BEZIG: 'Bezig',
  NIET_VAN_TOEPASSING: 'Niet van toepassing',
  GEBLOKKEERD: 'Geblokkeerd',
  HEROPENEN: 'Heropenen',
}

/** Acties waarbij een reden altijd verplicht is. */
export const ACTIEPUNT_ACTIES_MET_REDEN = ['NIET_VAN_TOEPASSING', 'GEBLOKKEERD'] as const satisfies readonly ActiepuntStatusActie[]

const redenSchema = z.string().trim().max(2000, { message: 'Maximaal 2000 tekens' })

export const actiepuntStatusActieSchema = z
  .object({
    actie: z.enum(ACTIEPUNT_STATUS_ACTIES),
    reden: redenSchema.optional(),
  })
  .superRefine((v, ctx) => {
    if ((ACTIEPUNT_ACTIES_MET_REDEN as readonly string[]).includes(v.actie) && !v.reden) {
      ctx.addIssue({ code: 'custom', path: ['reden'], message: 'Geef een reden op' })
    }
  })
export type ActiepuntStatusActieInput = z.infer<typeof actiepuntStatusActieSchema>

/**
 * De foutcode als iemand afvinkt terwijl de meting "niet gereed" zegt en er geen
 * reden bij staat (spec 214 §5.1). De api stuurt hem als 400 met een veldfout
 * op `reden`; het formulier toont dan de waarschuwing bij het redenveld.
 */
export const ACTIEPUNT_REDEN_VEREIST = 'ACTIEPUNT_REDEN_VEREIST'

export const actiepuntBesluitSchema = z.object({
  keuze: z.string().trim().min(1, { message: 'Vul het besluit in' }).max(200, { message: 'Maximaal 200 tekens' }),
  toelichting: z.string().trim().max(2000, { message: 'Maximaal 2000 tekens' }).optional(),
})
export type ActiepuntBesluitInput = z.infer<typeof actiepuntBesluitSchema>

export const actiepuntOpmerkingSchema = z.object({
  opmerking: z.string().trim().min(1, { message: 'Schrijf een opmerking' }).max(2000, { message: 'Maximaal 2000 tekens' }),
})
export type ActiepuntOpmerkingInput = z.infer<typeof actiepuntOpmerkingSchema>

/** Toewijzen + de door beheer instelbare velden (rol, prioriteit, deadline). */
export const actiepuntToewijzenSchema = z
  .object({
    rol: z.enum(ACTIEPUNT_ROLLEN).optional(),
    toegewezenAanId: z.string().min(1).max(64).nullable().optional(),
    prioriteit: z.enum(ACTIEPUNT_PRIORITEITEN).optional(),
    deadline: actiepuntDatumSchema.nullable().optional(),
  })
  .refine((v) => Object.values(v).some((w) => w !== undefined), {
    message: 'Er is niets gewijzigd',
  })
export type ActiepuntToewijzenInput = z.infer<typeof actiepuntToewijzenSchema>

/** Handmatig een punt toevoegen (spec 214 §7): dezelfde velden als de seed. */
export const actiepuntAanmakenSchema = z
  .object({
    titel: z.string().trim().min(3, { message: 'Minimaal 3 tekens' }).max(200, { message: 'Maximaal 200 tekens' }),
    omschrijving: z.string().trim().min(10, { message: 'Beschrijf in minimaal 10 tekens wat er moet gebeuren' }).max(4000),
    soort: z.enum(ACTIEPUNT_SOORTEN),
    module: z.enum(ACTIEPUNT_MODULES),
    rol: z.enum(ACTIEPUNT_ROLLEN),
    prioriteit: z.enum(ACTIEPUNT_PRIORITEITEN),
    deadline: actiepuntDatumSchema.optional(),
    deeplink: actiepuntDeeplinkSchema.optional(),
    check: actiepuntCheckVerwijzingSchema.optional(),
    bronVerwijzing: z.string().trim().max(300).optional(),
  })
export type ActiepuntAanmakenInput = z.infer<typeof actiepuntAanmakenSchema>

// ─── DTO's ──────────────────────────────────────────────────────────────────

export interface ActiepuntPersoonDTO {
  id: string
  naam: string
}

export interface ActiepuntMetingDTO {
  uitkomst: ActiepuntMetingUitkomst
  /** ISO-moment van de laatste meting. */
  op: string
  detail: string | null
}

export interface ActiepuntBesluitDTO {
  keuze: string
  toelichting: string | null
  door: ActiepuntPersoonDTO | null
  op: string
  specAanname: string | null
}

export interface ActiepuntRijDTO {
  id: string
  code: string
  titel: string
  soort: ActiepuntSoort
  nevenSoorten: ActiepuntSoort[]
  module: string
  moduleLabel: string
  bronBatch: number | null
  bronFo: string | null
  bronVerwijzing: string | null
  inventarisNr: number | null
  verantwoordelijkeRol: ActiepuntRol
  toegewezenAan: ActiepuntPersoonDTO | null
  /** 'yyyy-MM-dd' of null. */
  deadline: string | null
  deadlineVerstreken: boolean
  prioriteit: ActiepuntPrioriteit
  deeplink: string | null
  status: ActiepuntStatus
  statusReden: string | null
  teControleren: boolean
  gereedBron: ActiepuntGereedBron | null
  afgerondOp: string | null
  /** null bij een automatische vaststelling (dan is het de meting). */
  afgerondDoor: ActiepuntPersoonDTO | null
  heropendOp: string | null
  heropendTeller: number
  heeftCheck: boolean
  checkLabel: string | null
  geenCheckReden: string | null
  meting: ActiepuntMetingDTO | null
  besluit: ActiepuntBesluitDTO | null
  herkomst: ActiepuntHerkomst
  vervallenVerwijzing: string | null
  /** Mag de kijker dit punt behandelen (de regel van de api). */
  magBehandelen: boolean
  /** Mag de kijker het besluit vastleggen (alleen bij een beslispunt). */
  magBesluiten: boolean
  /** Mag de kijker toewijzen (rol, persoon, prioriteit, deadline): ADMIN of MANAGER. */
  magToewijzen: boolean
}

export interface ActiepuntAuditRegelDTO {
  id: string
  actie: ActiepuntAuditActie
  oudStatus: ActiepuntStatus | null
  nieuwStatus: ActiepuntStatus | null
  automatisch: boolean
  actor: ActiepuntPersoonDTO | null
  metingUitkomst: ActiepuntMetingUitkomst | null
  detail: string | null
  op: string
}

export interface ActiepuntDetailDTO extends ActiepuntRijDTO {
  omschrijving: string
  specAanname: string | null
  checkSleutel: string | null
  /** Het spoor, nieuwste eerst (de laatste regels; `auditTotaal` telt alles). */
  audit: ActiepuntAuditRegelDTO[]
  auditTotaal: number
  /** Alleen een handmatig toegevoegd punt, door wie het toevoegde of ADMIN. */
  magVerwijderen: boolean
}

export interface ActiepuntenLijstMetaDTO {
  total: number
  page: number
  pageSize: number
  totalPages: number
}

export interface ActiepuntTellersDTO {
  /** Nog te doen (OPEN, BEZIG, GEBLOKKEERD). */
  open: number
  /** Open per verantwoordelijke rol. */
  openPerRol: Record<ActiepuntRol, number>
  geblokkeerd: number
  /** Open punten met prioriteit "blokkeert een functie". */
  blokkerend: number
  /** Punten waarvan de laatste controle mislukte (alle statussen, zoals het filter "meting"). */
  controleMislukt: number
  /** Punten waarvan de controle niet (meer) bestaat: een sleutel die de registry niet kent. */
  controleOnbekend: number
  /** Open punten met de inventaris-markering "te controleren". */
  teControleren: number
  /** Open punten die de meting ooit heeft heropend. */
  heropend: number
  gereed: number
  nietVanToepassing: number
  /** Open punten voor de kijker (toegewezen of zijn rol). */
  voorMij: number
  /** ISO-moment van de laatste meting over het register, of null. */
  laatsteMetingOp: string | null
  /**
   * ISO-moment van de laatste GEPLANDE ronde die echt gemeten heeft (dagelijkse
   * cron of de ronde na een start; geen "Nu controleren", geen overgeslagen
   * ronde), of null. Hierop slaat de waarschuwing "de meting draait niet" aan.
   */
  laatsteGeplandeMetingOp: string | null
  /** De bron-batches die in het register voorkomen (voor het filter). */
  batches: number[]
}

export interface ActiepuntDashboardDTO {
  /** Open punten voor de kijker. */
  voorMij: number
  /** Waarvan met prioriteit "blokkeert een functie". */
  blokkerend: number
  /** De eerste paar (zwaarste eerst), voor de kaart. */
  eerste: Array<{ id: string; code: string; titel: string }>
}

/** Een check zoals het formulier hem kan kiezen. */
export interface ActiepuntCheckOptieDTO {
  sleutel: string
  label: string
  omschrijving: string
  /** Leesbare omschrijving van de parameters (geen vrije sleutels). */
  paramsUitleg: string | null
  /**
   * De check werkt zonder parameters. Alleen zulke checks kiest het formulier
   * "Punt toevoegen": een check met parameters (een meldingssoort, een klant)
   * hoort in de seed van een oplevering, niet in een vrij tekstveld.
   */
  zonderParameters: boolean
}

/** Uitkomst van "Nu controleren". */
export interface ActiepuntMetingResultaatDTO {
  gemeten: number
  gereed: number
  automatischGereed: number
  heropend: number
  mislukt: number
  nietAutomatisch: number
  /** Punten waarvan het vastleggen mislukte (een technische fout): niets veranderd. */
  nietVastgelegd: number
  nieuwePunten: number
  /** Seed-punten of -bestanden die niet konden worden ingelezen (de meting ging door). */
  seedFouten: number
  overgeslagen: boolean
  /**
   * De ronde duurde langer dan het verzoek wacht en loopt op de achtergrond door
   * (dan zijn de tellingen nog leeg): de uitkomst staat straks in de lijst.
   */
  loptNog: boolean
}

// ─── Weergave-helpers ───────────────────────────────────────────────────────

/**
 * "Handmatig vastgesteld door X op 12-10-2026" en varianten: de expliciete
 * herkomst van een afgerond punt (de regel "nooit stil"). Ook voor "niet van
 * toepassing": wie dat vaststelde en wanneer (een vervallen punt uit een seed
 * heeft geen persoon; de reden staat erbij).
 */
export function actiepuntGereedZin(punt: {
  status: ActiepuntStatus
  gereedBron: ActiepuntGereedBron | null
  afgerondOpNl: string | null
  afgerondDoorNaam: string | null
  heeftCheck: boolean
  /** Een punt uit de terugwerkende inventaris van FO-514 heeft een nummer; een later punt niet. */
  inventarisNr?: number | null
  bronBatch?: number | null
}): string | null {
  const op = punt.afgerondOpNl ? ` op ${punt.afgerondOpNl}` : ''
  const door = punt.afgerondDoorNaam ? ` door ${punt.afgerondDoorNaam}` : ''
  if (punt.status === 'NIET_VAN_TOEPASSING') {
    return door || op ? `Niet van toepassing, vastgesteld${door}${op}` : null
  }
  if (punt.status !== 'GEREED' || !punt.gereedBron) return null
  switch (punt.gereedBron) {
    case 'AUTOMATISCH':
      return `Automatisch vastgesteld door de meting${op}`
    case 'HANDMATIG':
      return `Handmatig vastgesteld${door}${op}`
    case 'HANDMATIG_AFWIJKEND':
      return `Handmatig vastgesteld${door}${op}, afwijkend van de meting`
    case 'BESLUIT':
      return `Besluit vastgelegd${door}${op}`
    case 'INVENTARIS': {
      // Gereed binnengekomen: uit de inventaris van FO-514, of uit het rapport van een latere batch.
      const bron =
        punt.inventarisNr !== null && punt.inventarisNr !== undefined
          ? 'de inventaris van FO-514'
          : punt.bronBatch
            ? `het rapport van batch ${punt.bronBatch}`
            : 'het rapport'
      return punt.heeftCheck
        ? `Gereed volgens ${bron}${op}; de eerstvolgende meting bevestigt of heropent het`
        : `Vastgesteld door ${bron}${op} (rapport, geen meting mogelijk)`
    }
  }
}
